import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Close,
  Headphones,
  MusicNote,
  OpenInNew,
  Pause,
  PlayArrow,
  Repeat,
  RepeatOne,
  Shuffle,
  SkipNext,
  SkipPrevious,
  VolumeOff,
  VolumeUp,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SlideUpTransition from './SlideUpTransition';
import { musicTracks } from './musicTracks';
import type { MusicTrack, SpotifyKind } from './musicTracks';
import { fetchOwnerRecentlyPlayed, isOwnerRecentlyPlayedAvailable } from '../../services/ownerSpotify';
import type { SpotifyRecentlyPlayedItem } from '../../services/ownerSpotify';
import './musicPlayer.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Converts a public Spotify URL (`https://open.spotify.com/track/…`)
 * or URI (`spotify:track:…`) into an embeddable `kind` + `id` pair.
 */
function parseSpotifyUrl(input: string): { kind: SpotifyKind; id: string } | null {
  const uri = input.trim();
  const uriMatch = uri.match(/^spotify:(track|album|playlist):([A-Za-z0-9]+)/);
  if (uriMatch) return { kind: uriMatch[1] as SpotifyKind, id: uriMatch[2] };

  const urlMatch = uri.match(/open\.spotify\.com\/(?:embed\/)?(track|album|playlist)\/([A-Za-z0-9]+)/);
  if (urlMatch) return { kind: urlMatch[1] as SpotifyKind, id: urlMatch[2] };

  return null;
}

type RecentsStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

/** Maps a Spotify "recently played" result to a track the player understands. */
function spotifyRecentToTrack(item: SpotifyRecentlyPlayedItem): MusicTrack {
  return {
    id: `recent-${item.id}`,
    title: item.title,
    artist: item.artist,
    album: item.album || undefined,
    artwork: item.artwork || undefined,
    duration: formatTime(item.durationSeconds),
    source: { type: 'spotify', spotifyUrl: item.spotifyUrl },
  };
}

const artworkGradients = [
  'linear-gradient(135deg, #1db954 0%, #191414 100%)',
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
];

function artworkStyle(track: MusicTrack): React.CSSProperties {
  if (track.artwork) {
    return { backgroundImage: `url("${track.artwork}")` };
  }
  const hash = track.id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return { backgroundImage: artworkGradients[hash % artworkGradients.length] };
}


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type RepeatMode = 'off' | 'all' | 'one';

export interface MusicPlayerPanelProps {
  /** Whether to try autoplaying the first royalty-free demo track */
  autoplayDemo?: boolean;
}

function MusicPlayerPanel({ autoplayDemo = true }: MusicPlayerPanelProps) {
  const { t } = useTranslation();

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [tracks, setTracks] = useState<MusicTrack[]>(musicTracks);
  const [recentsStatus, setRecentsStatus] = useState<RecentsStatus>('idle');
  const [recentsResolved, setRecentsResolved] = useState(false);
  const [currentTrackId, setCurrentTrackId] = useState<string>(musicTracks[0]?.id ?? '');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState<RepeatMode>('off');

  const currentTrack = useMemo<MusicTrack | undefined>(
    () => tracks.find((track) => track.id === currentTrackId) ?? tracks[0],
    [currentTrackId, tracks],
  );
  const currentIndex = Math.max(
    0,
    tracks.findIndex((track) => track.id === currentTrack?.id),
  );
  const hasRecentTracks = tracks.some((track) => track.id.startsWith('recent-'));
  const isSpotifyTrack = currentTrack?.source.type === 'spotify';
  const spotifyInfo =
    currentTrack && currentTrack.source.type === 'spotify'
      ? parseSpotifyUrl(currentTrack.source.spotifyUrl)
      : null;

  const playTrackAt = useCallback((index: number) => {
    const track = tracks[index];
    if (!track) return;
    setCurrentTrackId(track.id);
    setCurrentTime(0);
    setDuration(0);

    const audio = audioRef.current;
    if (track.source.type === 'file' && audio) {
      audio.src = track.source.src;
      audio.currentTime = 0;
      void audio.play().catch(() => setIsPlaying(false));
    } else if (audio) {
      // Spotify tracks are played inside their own embed widget
      audio.pause();
      audio.removeAttribute('src');
    }
  }, [tracks]);

  const pickNextIndex = useCallback(
    (fromIndex: number): number => {
      if (tracks.length <= 1) return fromIndex;
      if (shuffle) {
        let next = fromIndex;
        while (next === fromIndex) {
          next = Math.floor(Math.random() * tracks.length);
        }
        return next;
      }
      return (fromIndex + 1) % tracks.length;
    },
    [shuffle, tracks],
  );

  // Wire up the hidden <audio> element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      if (repeat === 'one') {
        audio.currentTime = 0;
        void audio.play().catch(() => setIsPlaying(false));
        return;
      }
      if (repeat === 'all' || shuffle) {
        playTrackAt(pickNextIndex(currentIndex));
        return;
      }
      if (currentIndex < tracks.length - 1) {
        playTrackAt(currentIndex + 1);
      } else {
        setIsPlaying(false);
        setCurrentTime(0);
      }
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, [currentIndex, repeat, shuffle, pickNextIndex, playTrackAt, tracks, volume]);

  // Pause any playback when the dialog unmounts
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      audio?.pause();
    };
  }, []);

  // Best-effort autoplay of the first royalty-free track once the player
  // opens, but only once we know there are no owner recents to show instead
  // (Spotify content is never autoplayed).
  useEffect(() => {
    if (!autoplayDemo || !recentsResolved) return;
    const first = tracks[0];
    const audio = audioRef.current;
    if (!first || first.source.type !== 'file' || !audio) return;

    audio.src = first.source.src;
    audio.currentTime = 0;
    const id = window.setTimeout(() => {
      void audio.play().catch(() => setIsPlaying(false));
    }, 400);

    return () => window.clearTimeout(id);
  }, [autoplayDemo, recentsResolved, tracks]);

  // Load the OWNER's recently played tracks (fetched to Firestore in the
  // background by a scheduled GitHub Actions job) when the player mounts.
  // Visitors never need a Spotify login to see them.
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!isOwnerRecentlyPlayedAvailable()) {
        if (!cancelled) {
          setRecentsStatus('unavailable');
          setRecentsResolved(true);
        }
        return;
      }

      if (!cancelled) setRecentsStatus('loading');
      try {
        const items = await fetchOwnerRecentlyPlayed();
        if (cancelled) return;

        if (items.length > 0) {
          const recentTracks = items.map(spotifyRecentToTrack);
          const audio = audioRef.current;
          if (audio) {
            audio.pause();
            audio.removeAttribute('src');
          }
          // The owner's history replaces the CC0 demo playlist completely —
          // the demo entries are only a fallback for when no history could be
          // loaded (no Firebase config, empty document or a read error).
          setTracks(recentTracks);
          setCurrentTrackId(recentTracks[0].id);
          setCurrentTime(0);
          setDuration(0);
          setIsPlaying(false);
        }
        if (!cancelled) setRecentsStatus('ready');
      } catch {
        if (!cancelled) setRecentsStatus('error');
      } finally {
        if (!cancelled) setRecentsResolved(true);
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      if (!audio.src && currentTrack?.source.type === 'file') {
        audio.src = currentTrack.source.src;
      }
      void audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  }, [currentTrack]);

  const handleNext = useCallback(() => {
    playTrackAt(pickNextIndex(currentIndex));
  }, [currentIndex, pickNextIndex, playTrackAt]);

  const handlePrev = useCallback(() => {
    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0;
      return;
    }
    if (tracks.length === 0) return;
    const prevIndex = (currentIndex - 1 + tracks.length) % tracks.length;
    playTrackAt(prevIndex);
  }, [currentIndex, playTrackAt, tracks]);

  const handleSeek = (_event: Event, value: number | number[]) => {
    const next = Array.isArray(value) ? value[0] : value;
    setCurrentTime(next);
    const audio = audioRef.current;
    if (audio) audio.currentTime = next;
  };

  const handleVolume = (_event: Event, value: number | number[]) => {
    const next = Array.isArray(value) ? value[0] : value;
    setVolume(next / 100);
  };

  return (
    <div className="music-player">
      <audio ref={audioRef} preload="metadata" />
      {/* Header */}
        <div className="mp-header">
          <div className="mp-brand">
            <span className="mp-brand-icon">
              <Headphones />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="mp-title">{t('musicPlayer.title')}</div>
              <div className="mp-subtitle">{t('musicPlayer.subtitle')}</div>
            </div>
          </div>
        </div>

        {(recentsStatus === 'loading' || recentsStatus === 'error') && (
          <div className="mp-connect-bar">
            <div className="mp-connect-left">
              <span className="mp-connect-dot" aria-hidden="true" />
              <span className="mp-connect-text">
                {recentsStatus === 'loading'
                  ? t('musicPlayer.loadingHistory')
                  : t('musicPlayer.historyUnavailable')}
              </span>
            </div>
          </div>
        )}

        {/* Now playing — no separate track header here: the Spotify embed
            already shows artwork, title and artist, and the highlighted row in
            the list below shows what is playing. */}
        {currentTrack && (
          <div className="mp-now-playing">
            {isSpotifyTrack ? (
              <div className="mp-spotify-embed">
                {spotifyInfo ? (
                  <iframe
                    key={spotifyInfo.id}
                    title={currentTrack.title}
                    src={`https://open.spotify.com/embed/${spotifyInfo.kind}/${spotifyInfo.id}?utm_source=generator&theme=0`}
                    width="100%"
                    height={152}
                    frameBorder={0}
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                  />
                ) : (
                  <p className="mp-time">{t('musicPlayer.invalidSpotifyUrl')}</p>
                )}
                <div className="mp-spotify-actions">
                  <span>{t('musicPlayer.spotifyPoweredNote')}</span>
                  {currentTrack.source.type === 'spotify' && (
                    <a
                      className="mp-spotify-link"
                      href={currentTrack.source.spotifyUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <OpenInNew fontSize="small" />
                      {t('musicPlayer.openInSpotify')}
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="mp-transport">
                {/* Progress */}
                <div className="mp-progress-row">
                  <span className="mp-time">{formatTime(currentTime)}</span>
                  <Slider
                    className="mp-slider"
                    size="small"
                    min={0}
                    max={duration || 1}
                    step={0.5}
                    value={Math.min(currentTime, duration || 1)}
                    onChange={handleSeek}
                    disabled={!duration}
                    aria-label={t('musicPlayer.seek')}
                  />
                  <span className="mp-time">{formatTime(duration)}</span>
                </div>

                {/* Transport buttons */}
                <div className="mp-buttons">
                  <IconButton
                    className={`mp-icon-button ${shuffle ? 'active' : ''}`}
                    size="small"
                    onClick={() => setShuffle((s) => !s)}
                    title={t('musicPlayer.shuffle')}
                    aria-label={t('musicPlayer.shuffle')}
                  >
                    <Shuffle />
                  </IconButton>
                  <IconButton
                    className="mp-icon-button"
                    size="small"
                    onClick={handlePrev}
                    title={t('musicPlayer.previous')}
                    aria-label={t('musicPlayer.previous')}
                  >
                    <SkipPrevious fontSize="large" />
                  </IconButton>
                  <IconButton
                    className="mp-play-button"
                    onClick={togglePlayPause}
                    title={isPlaying ? t('musicPlayer.pause') : t('musicPlayer.play')}
                    aria-label={isPlaying ? t('musicPlayer.pause') : t('musicPlayer.play')}
                  >
                    {isPlaying ? <Pause fontSize="large" /> : <PlayArrow fontSize="large" />}
                  </IconButton>
                  <IconButton
                    className="mp-icon-button"
                    size="small"
                    onClick={handleNext}
                    title={t('musicPlayer.next')}
                    aria-label={t('musicPlayer.next')}
                  >
                    <SkipNext fontSize="large" />
                  </IconButton>
                  <IconButton
                    className={`mp-icon-button ${repeat !== 'off' ? 'active' : ''}`}
                    size="small"
                    onClick={() =>
                      setRepeat((r) => (r === 'off' ? 'all' : r === 'all' ? 'one' : 'off'))
                    }
                    title={t('musicPlayer.repeat')}
                    aria-label={t('musicPlayer.repeat')}
                  >
                    {repeat === 'one' ? <RepeatOne /> : <Repeat />}
                  </IconButton>
                </div>

                {/* Volume */}
                <div className="mp-volume-row">
                  {volume === 0 ? <VolumeOff /> : <VolumeUp />}
                  <Slider
                    className="mp-slider"
                    size="small"
                    min={0}
                    max={100}
                    value={Math.round(volume * 100)}
                    onChange={handleVolume}
                    aria-label={t('musicPlayer.volume')}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Track list */}
        <div className="mp-tracklist">
          {hasRecentTracks && (
            <div className="mp-list-heading">{t('musicPlayer.recentlyPlayed')}</div>
          )}
          {tracks.map((track, index) => {
            const isActive = track.id === currentTrack?.id;
            const isSpotify = track.source.type === 'spotify';
            return (
              <button
                key={track.id}
                className={`mp-row ${isActive ? 'active' : ''}`}
                type="button"
                onClick={() => {
                  if (isActive && !isSpotify) {
                    togglePlayPause();
                  } else if (!isActive) {
                    playTrackAt(index);
                  }
                }}
              >
                <span className="mp-row-index">
                  {isActive && isPlaying && !isSpotify ? '▶' : index + 1}
                </span>
                <span className="mp-row-art" style={artworkStyle(track)}>
                  {!track.artwork && <MusicNote />}
                </span>
                <span className="mp-row-meta">
                  <span className="mp-row-title">{track.title}</span>
                  <span className="mp-row-artist">{track.artist}</span>
                </span>
                <span className={`mp-row-badge ${isSpotify ? 'spotify' : 'file'}`}>
                  {isSpotify ? t('musicPlayer.spotifyBadge') : t('musicPlayer.fileBadge')}
                </span>
                <span className="mp-row-duration">{track.duration ?? '—'}</span>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mp-footer">
          <p>{t('musicPlayer.legalNote')}</p>
        </div>
    </div>
  );
}

interface MusicPlayerDialogProps {
  open: boolean;
  onClose: () => void;
}

function MusicPlayerDialog({ open, onClose }: MusicPlayerDialogProps) {
  const isCompactScreen = useMediaQuery('(max-width: 768px)');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isCompactScreen}
      TransitionComponent={SlideUpTransition}
      sx={{
        '& .MuiDialog-paper': {
          margin: isCompactScreen ? 0 : 2,
          width: isCompactScreen ? '100vw' : 'min(96vw, 500px)',
          height: isCompactScreen ? '100dvh' : 'min(92vh, 720px)',
          maxWidth: isCompactScreen ? '100vw' : 'min(96vw, 500px)',
          maxHeight: isCompactScreen ? '100dvh' : 'min(92vh, 720px)',
          borderRadius: isCompactScreen ? 0 : 3,
          overflow: 'hidden',
          background: '#121212',
        },
      }}
    >
      <IconButton
        aria-label="Close dialog"
        onClick={onClose}
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 20,
          color: '#fff',
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.62)' },
        }}
      >
        <Close fontSize="small" />
      </IconButton>
      <MusicPlayerPanel />
    </Dialog>
  );
}

export default MusicPlayerDialog;
export { MusicPlayerPanel };
